const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/$/, '');
const ROLES_API_BASE = `${BACKEND_URL}/roles`;

export const getRolesHeaders = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const tenantId =
    typeof window !== 'undefined'
      ? localStorage.getItem('tenantId') || localStorage.getItem('tenant_id')
      : null;
  const h = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  if (tenantId) h['X-tenant-id'] = tenantId;
  // Do NOT send db_name / TENANT_DB_PATH — non-standard headers break CORS ("Failed to fetch").
  // Roles-service resolves schema from X-tenant-id (and SidebarMenuService falls back by tenantId).
  return h;
};

async function parseError(res, fallback) {
  const text = await res.text();
  try {
    const json = JSON.parse(text);
    return json.message || json.error || text || fallback;
  } catch {
    return text || fallback;
  }
}

/** CDX catalog list (includes inactive / platform when requested). */
export async function fetchMasterMenus({
  includePlatformOnly = true,
  includeInactive = true,
  productCode,
} = {}) {
  const params = new URLSearchParams({
    includePlatformOnly: String(includePlatformOnly),
    includeInactive: String(includeInactive),
  });
  if (productCode) params.set('productCode', productCode);
  const res = await fetch(`${ROLES_API_BASE}/master-menus?${params}`, {
    headers: getRolesHeaders(),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to load master menus (${res.status})`));
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function createMasterMenu(body) {
  const res = await fetch(`${ROLES_API_BASE}/master-menus`, {
    method: 'POST',
    headers: getRolesHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to create menu (${res.status})`));
  return res.json();
}

export async function updateMasterMenu(menuId, body) {
  const res = await fetch(`${ROLES_API_BASE}/master-menus/${menuId}`, {
    method: 'PUT',
    headers: getRolesHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to update menu (${res.status})`));
  return res.json();
}

export async function setMasterMenuActive(menuId, active) {
  const res = await fetch(`${ROLES_API_BASE}/master-menus/${menuId}/active`, {
    method: 'PUT',
    headers: getRolesHeaders(),
    body: JSON.stringify({ active: !!active }),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to update active flag (${res.status})`));
  return res.json();
}

export async function deleteMasterMenu(menuId) {
  const res = await fetch(`${ROLES_API_BASE}/master-menus/${menuId}`, {
    method: 'DELETE',
    headers: getRolesHeaders(),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to delete menu (${res.status})`));
  return true;
}

/** Menus available to assign; each row includes assigned/enabled for this tenant. */
export async function fetchAvailableTenantMenus(tenantId, { includePlatformOnly = false } = {}) {
  const qs = includePlatformOnly ? '?includePlatformOnly=true' : '?includePlatformOnly=false';
  const res = await fetch(`${ROLES_API_BASE}/tenants/${tenantId}/menus/available${qs}`, {
    headers: getRolesHeaders(),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to load menus (${res.status})`));
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function fetchTenantMenus(tenantId, { enabledOnly = true } = {}) {
  const qs = enabledOnly ? '?enabledOnly=true' : '?enabledOnly=false';
  const res = await fetch(`${ROLES_API_BASE}/tenants/${tenantId}/menus${qs}`, {
    headers: getRolesHeaders(),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to load tenant menus (${res.status})`));
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Dynamic sidebar menus for the logged-in tenant.
 * CDX: full master catalog. Tenant: pass userId so Access is applied server-side.
 */
export async function fetchSidebarMenus(
  tenantId,
  { includePlatformOnly = false, cdxUser = false, userId = null } = {}
) {
  const params = new URLSearchParams({
    includePlatformOnly: String(!!includePlatformOnly),
    cdxUser: String(!!cdxUser),
  });
  if (userId != null && userId !== '') {
    params.set('userId', String(userId));
  }
  const res = await fetch(`${ROLES_API_BASE}/tenants/${tenantId}/menus/sidebar?${params}`, {
    headers: getRolesHeaders(),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to load sidebar menus (${res.status})`));
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function saveTenantMenus(tenantId, menuIds) {
  const res = await fetch(`${ROLES_API_BASE}/tenants/${tenantId}/menus`, {
    method: 'PUT',
    headers: getRolesHeaders(),
    body: JSON.stringify({
      tenantId: Number(tenantId),
      menuIds: menuIds || [],
    }),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to save menus (${res.status})`));
  return res.json();
}

/** Ensure DDL + architecture seed (upsert by menu_code). */
export async function ensureMasterMenus() {
  const res = await fetch(`${ROLES_API_BASE}/master-menus/ensure`, {
    method: 'POST',
    headers: getRolesHeaders(),
  });
  if (!res.ok) throw new Error(await parseError(res, `Failed to ensure master menus (${res.status})`));
  return res.json();
}

export const PRODUCT_CODE_OPTIONS = [
  { code: 'HUB', name: 'Hub' },
  { code: 'SYNC_FILE', name: 'Sync File' },
  { code: 'AGENTIC_AI', name: 'AI Agents' },
  { code: 'PLATFORM', name: 'Platform' },
];
